// The run stream lifecycle — the bulk of the pre-carve
// `src/features/agent-harness/hooks.ts`, and the last thing holding this
// family's `query` dimension amber.
//
// History: partial extraction —
// `git log --follow -- src/features/agent-harness/hooks.ts`.
//
// WHAT IT WAS BLOCKED ON, and how each blocker was severed rather than argued
// away:
//
//   `EventDedup` via `useAgentStream` — the user-wide event mechanism whose
//   separation was this milestone's own stated precondition. It moved into this
//   package with the user-wide-events carve; the leak is closed, not routed
//   around.
//
//   `stream-http`'s header minting — the auth token and the `X-Client-*` pair.
//   Still app-side, still may not live here, and now crossing as a REQUIRED
//   parameter on the stream hook exactly as `transport` does on the run query.
//
//   A failed-cancel `toast`. It is NOT a mutation-definition toast, so `meta`
//   cannot carry it: the pre-carve body raised it inside the per-call
//   `cancelMutate(id, { onError })` options, and putting it on
//   `useMutateCancelTurn`'s `meta` would toast for every consumer of that
//   mutation rather than for this one call site. It crosses as the
//   `onCancelFailed` port, fired exactly where `toast(...)` fired and after the
//   same reconciling `refetch()`.
//
// The transcript is NOT composed here: snapshot, stream and optimistic submit
// are three ingestors into one identity-keyed store
// (`../harness/transcript-store`) and the render is a pure fold. The reducer,
// the two refs that decide whether a cancel was local, the boundary handling
// and the cancel-timeout fallback move intact, because every one of
// them is behaviour a rewrite would quietly change.
//
// This file is the composition point: the transcript store seam, the optimistic
// submit machinery and the cancel flow each live in their own hook beside it.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { useLatestRef } from '@heidi/utils';

import {
  inboxQueryKeys,
  toolApprovalQueryKeys,
  type ChatItem,
  type LLMStreamConnectionStatus,
  type LLMStreamEvent,
  type Message,
  type PublicRunSnapshot,
  type RunTransport,
  type StreamDiagnostics,
  type StreamHeadersFactory,
} from '../../contracts';
import { useAgentStream } from '../harness/stream/use-agent-stream';
import { isLatestCancelPreOutput } from '../harness/timeline/cancel-markers';
import { foldTranscript, ingestStreamEvent } from '../harness/transcript-store';
import { createTurnBoundaryWitness } from '../harness/turn-boundary-witness';
import {
  INITIAL_TURN_LIFECYCLE,
  isTurnInFlight,
  turnLifecycleReducer,
} from '../harness/turn-lifecycle';
import {
  computeRunRefetchInterval,
  isTerminalRunStatus,
  runQueryOptions,
} from './useQueryRun';
import { useRunCancellation } from './useRunCancellation';
import { useTranscriptStoreState } from './useTranscriptStoreState';
import {
  useTurnSubmission,
  type TurnOperationHandle,
  type TurnSubmissionHandle,
} from './useTurnSubmission';

export type { TurnOperationHandle, TurnSubmissionHandle };

export interface RunStreamLifecycleResult {
  run: PublicRunSnapshot | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  items: ChatItem[];
  turnInFlight: boolean;
  cancelling: boolean;
  streamStatus: LLMStreamConnectionStatus;
  streamDiagnostics: StreamDiagnostics | null;
  /**
   * Render a turn optimistically and take a handle on that submission, settled
   * with `accept()` / `reject()` when its POST resolves. Nothing is treated as
   * backend-known until it is accepted. Pass `turnId` when the turn was minted
   * at enqueue, so a queued message keeps one identity end to end.
   */
  handleTurnSubmitted: (
    messages: Message[],
    options?: { turnId?: string }
  ) => TurnSubmissionHandle;
  /**
   * Register a local operation before any upload/permission await or POST.
   * Existing composer paths can keep using `handleTurnSubmitted`.
   */
  beginTurnOperation: (options?: { turnId?: string }) => TurnOperationHandle;
  handleCancelTurn: () => void;
  /** Contextual loader phrases for the active turn, when pushed via agent-events. */
  loaderPhrases: string[] | undefined;
  handleLoaderPhrases: (turnId: string, phrases: string[]) => void;
}

export type RunStreamEventObserver = (id: string, event: LLMStreamEvent) => void;

export interface UseRunStreamLifecycleOptions {
  onStreamEvent?: RunStreamEventObserver;
  /**
   * Fired when a turn was cancelled *before the agent produced any output* — a
   * pre-output stop (the refetched snapshot ends in a `turn_cancelled` marker
   * directly after the user prompt). Opt-in: surfaces are free to ignore it.
   * Projects uses it to return the typed prompt to the composer. NOT fired for a
   * mid-stream cancel (the partial transcript + "Stopped" note are kept).
   */
  onTurnDiscarded?: () => void;
  /**
   * Drop pre-output cancelled turns (user bubble + `turn_cancelled` marker) from
   * the timeline. Opt-in; Projects pairs it with `onTurnDiscarded` so a
   * stop-before-output leaves no trace.
   */
  dropEmptyCancelledTurns?: boolean;
  /**
   * Keep the per-run `/stream` open across turn boundaries (follow mode) instead
   * of closing when the current turn ends. The stream then delivers turns started
   * on other devices too (cross-device sync), and a `turn_started` frame triggers
   * a snapshot refetch to pick up the remote turn's user message. Default: closed
   * per turn.
   */
  follow?: boolean;
  /** Keep polling non-terminal runs even between turns. Defaults to false. */
  pollWhileRunning?: boolean;
  /**
   * App-reaction port: fired where the failed-cancel `toast(...)` fired, after
   * the reconciling `refetch()`. Optional — a consumer that supplies nothing
   * still gets the reconcile, which is the part that is data behaviour.
   */
  onCancelFailed?: () => void;
  /**
   * REQUIRED, and it is a separate input rather than a read off `transport`
   * because `RunTransport.streamHeaders` is OPTIONAL by design: a non-Scribe
   * transport may omit it, and the pre-carve code then fell back to the Scribe
   * header factory — an app default this zone may not name. The delegate
   * resolves `transport.streamHeaders ?? streamHeaders` and passes the result,
   * so the fallback stays exactly where it was expressible.
   */
  streamHeaders: StreamHeadersFactory;
}

export const useRunStreamLifecycle = (
  agentRunId: string | undefined,
  transport: RunTransport,
  // No default: `streamHeaders` is required, so there is no valid empty options
  // object here. The delegate always supplies one.
  options: UseRunStreamLifecycleOptions
): RunStreamLifecycleResult => {
  const queryClient = useQueryClient();
  const onStreamEventRef = useLatestRef(options.onStreamEvent);
  const onTurnDiscardedRef = useLatestRef(options.onTurnDiscarded);
  const dropEmptyCancelledTurns = options.dropEmptyCancelledTurns ?? false;
  const follow = options.follow ?? false;
  const [turnLifecycle, dispatchTurn] = useReducer(
    turnLifecycleReducer,
    INITIAL_TURN_LIFECYCLE
  );
  const { phase } = turnLifecycle;
  const [loaderPhrasesByTurnId, setLoaderPhrasesByTurnId] = useState<
    Record<string, string[]>
  >({});

  // Reactive stream status so a transition to 'error' causes React Query to
  // re-evaluate refetchInterval and resume settlement polling. A plain ref
  // would not schedule that re-evaluation — the interval callback runs on
  // React Query's own schedule, not on every render, so the stale ref value
  // would persist across the 'idle → error' transition.
  const [streamStatus, setStreamStatus] = useState<LLMStreamConnectionStatus>('idle');
  const wasTurnActiveRef = useRef(false);
  const turnActivityRunIdRef = useRef(agentRunId);

  const query = useQuery({
    ...runQueryOptions(agentRunId, transport),
    refetchInterval: (currentQuery) =>
      computeRunRefetchInterval(
        currentQuery.state.data,
        phase,
        options.pollWhileRunning ?? false,
        streamStatus
      ),
  });

  const { data: run, error, isError, isLoading, refetch } = query;
  const streamCursor = run?.stream_cursor ?? null;

  const { store, applyToStore } = useTranscriptStoreState(agentRunId, run);

  const {
    handleTurnSubmitted: submitTurn,
    beginTurnOperation: beginTurn,
    settleNamedTerminal,
    settleUnnamedTerminal,
    abandonOutstandingOperations,
    resubscribeToken,
    isLocalTurnPending,
    resetTurnTracking,
  } = useTurnSubmission({ agentRunId, follow, applyToStore, refetch, dispatchTurn });
  const settleNamedRef = useLatestRef(settleNamedTerminal);
  const settleUnnamedRef = useLatestRef(settleUnnamedTerminal);
  const [boundaryWitness] = useState(() =>
    createTurnBoundaryWitness({
      settleNamed: (turnId, kind) => settleNamedRef.current(turnId, kind),
      settleUnnamed: () => settleUnnamedRef.current(),
    })
  );
  const handleTurnSubmitted = useCallback<
    RunStreamLifecycleResult['handleTurnSubmitted']
  >(
    (messages, submitOptions) => {
      boundaryWitness.armForNewTurn();
      return submitTurn(messages, submitOptions);
    },
    [boundaryWitness, submitTurn]
  );
  const beginTurnOperation = useCallback<RunStreamLifecycleResult['beginTurnOperation']>(
    (submitOptions) => {
      boundaryWitness.armForNewTurn();
      return beginTurn(submitOptions);
    },
    [beginTurn, boundaryWitness]
  );

  useEffect(() => {
    boundaryWitness.resetForRunChange();
    wasTurnActiveRef.current = false;
    abandonOutstandingOperations('run_changed');
    dispatchTurn({ type: 'RUN_CHANGED' });
    setLoaderPhrasesByTurnId({});
    resetTurnTracking();
  }, [abandonOutstandingOperations, agentRunId, boundaryWitness, resetTurnTracking]);

  useEffect(
    () => () => {
      abandonOutstandingOperations('unmounted');
    },
    [abandonOutstandingOperations]
  );

  const markStreamTurnBoundary = useCallback(
    (
      turnId: string | null,
      cursor: string,
      kind: 'completed' | 'cancelled' | 'failed'
    ) => {
      boundaryWitness.witnessStream(turnId, cursor, kind);
    },
    [boundaryWitness]
  );
  const markSnapshotTurnBoundary = useCallback(
    (cursor: string | null) => {
      boundaryWitness.witnessSnapshot(cursor);
    },
    [boundaryWitness]
  );

  // Refetch the snapshot and, if it ends in a pre-output cancel of a turn THIS
  // client submitted, fire `onTurnDiscarded`. Detection reads the resolved
  // refetch (not a reactive effect on `run`: React Query structural sharing can
  // hand back the SAME `run` reference when the post-cancel snapshot deep-equals
  // the cached one, silencing the effect). Shared by the SSE `turn_cancelled`
  // boundary and the cancel-timeout fallback so a missed boundary still recovers
  // the prompt. `wasLocal` is captured synchronously so a later boundary reset
  // can't change this turn's verdict.
  const refetchAndDetectDiscard = useCallback(() => {
    const wasLocal = isLocalTurnPending();
    void refetch().then((result) => {
      const messages = result.data?.messages;
      if (!messages || messages.length === 0) return;
      if (wasLocal && isLatestCancelPreOutput(messages)) {
        onTurnDiscardedRef.current?.();
      }
    });
  }, [refetch, onTurnDiscardedRef, isLocalTurnPending]);

  const handleStreamEvent = useCallback(
    (id: string, event: LLMStreamEvent) => {
      onStreamEventRef.current?.(id, event);

      // `turn_failed` is deliberate: the turn is over, and leaving the phase
      // active until a settled snapshot arrives strands Stop and the send owner.
      const isBoundary =
        event.type === 'turn_completed' ||
        event.type === 'turn_cancelled' ||
        event.type === 'turn_failed';

      // While cancelling we freeze the transcript at what was shown and only let
      // boundary events through (the cancel confirmation).
      if (phase !== 'cancelling' || isBoundary) {
        applyToStore((store) => ingestStreamEvent(store, { id, event }, streamCursor));
      }

      if (event.type === 'approval_resolved') {
        void queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
        void queryClient.invalidateQueries({ queryKey: toolApprovalQueryKeys.all });
      }

      if (event.type === 'turn_started') {
        boundaryWitness.armForNewTurn();
        // A turn just started on this run — possibly on another device (follow
        // mode). The user's message never travels on the stream, so refetch the
        // snapshot to pick it up; the snapshot's `turn_running` then drives
        // TURN_CONFIRMED and the composer's active state.
        void refetch();
      }

      // Terminal turn events, not `message_end`: the assistant row is written
      // after the stream drains, so a `message_end` refetch races that write and
      // reads a snapshot the reply has not landed in yet.
      if (
        event.type === 'turn_completed' ||
        event.type === 'turn_cancelled' ||
        event.type === 'turn_failed' ||
        // Hydrate run.approvals (approval_kind + override scopes) promptly.
        event.type === 'approval_requested' ||
        event.type === 'approval_resolved' ||
        event.type === 'error'
      ) {
        if (isBoundary) {
          // The turn is over. `ended` overrides the snapshot's stale
          // `turn_running` so the composer stops offering Stop and the stream
          // closes immediately — no doomed cancels, no spurious reconnect. It
          // also covers a cancel that loses the race to a terminal turn event.
          // Record THIS boundary's stream cursor (`id`, always present — the
          // stream drops id-less events). The `ended` suppression lifts once the
          // snapshot's own stream_cursor catches up past it, so the stale
          // just-ended turn can't escape while a genuinely new one (snapshot
          // already past the boundary) can.
          dispatchTurn({ type: 'BOUNDARY', cursor: id });
          markStreamTurnBoundary(
            event.turn_id ?? null,
            id,
            event.type === 'turn_cancelled'
              ? 'cancelled'
              : event.type === 'turn_failed'
                ? 'failed'
                : 'completed'
          );
        }
        if (event.type === 'turn_cancelled') {
          // The bubble is NOT withdrawn here: it is the same entry the
          // refetched snapshot upgrades, so it never flashes out and back.
          refetchAndDetectDiscard();
        } else {
          void refetch();
        }
        if (isBoundary) {
          // Uniform reset on every boundary (after the cancel detection above
          // captured `wasLocal`): the next turn is "local" only if it goes
          // through `handleTurnSubmitted` again. Bumping the submission id makes
          // an in-flight handle stale, so a late `accept()` can't re-claim it.
          resetTurnTracking();
        }
      }
    },
    [
      applyToStore,
      boundaryWitness,
      markStreamTurnBoundary,
      onStreamEventRef,
      phase,
      queryClient,
      refetch,
      refetchAndDetectDiscard,
      resetTurnTracking,
      streamCursor,
    ]
  );

  const turnActive = isTurnInFlight(phase, run?.input_state);

  // Rising activity arms the unnamed-backend fallback. On a falling edge with
  // no stream boundary, the settled snapshot is the only terminal evidence.
  useEffect(() => {
    if (turnActivityRunIdRef.current !== agentRunId) {
      turnActivityRunIdRef.current = agentRunId;
      wasTurnActiveRef.current = false;
      return;
    }
    const wasActive = wasTurnActiveRef.current;
    wasTurnActiveRef.current = turnActive;
    if (!wasActive && turnActive) {
      boundaryWitness.armForNewTurn();
      return;
    }
    if (wasActive && !turnActive && phase === 'idle') {
      markSnapshotTurnBoundary(run?.stream_cursor ?? null);
    }
  }, [
    agentRunId,
    boundaryWitness,
    markSnapshotTurnBoundary,
    phase,
    run?.stream_cursor,
    turnActive,
  ]);

  // Follow mode holds the stream open for the whole run (to catch turns started
  // on other devices); per-turn mode opens only while a turn is in flight.
  const shouldOpenStream =
    !!agentRunId && !!run && run.status === 'running' && (follow || turnActive);

  const stream = useAgentStream({
    agentRunId,
    enabled: shouldOpenStream,
    follow,
    lastEventId: streamCursor,
    // Stable across turns and remote boundaries: the cursor + dedup re-seed only
    // on a run change — not on every turn boundary (the old per-turn key flip
    // that forced a reconnect + dedup rebuild every turn is gone). In follow
    // mode a local submit additionally re-keys via `resubscribeToken` so a
    // submit made while the stream is mid-backoff reconnects at once.
    subscriptionKey: follow ? `${agentRunId ?? ''}:${resubscribeToken}` : agentRunId,
    streamPath: transport.streamPath,
    streamHeaders: options.streamHeaders,
    onEvent: handleStreamEvent,
  });

  useEffect(() => {
    setStreamStatus(stream.status);
  }, [stream.status]);

  // Follow mode holds the stream open on idle. The BE's `run_terminated`
  // frame surfaces here as stream `done` (in follow mode a transient close
  // reconnects instead of ending), but it's consumed inside `useAgentStream`
  // and never reaches `handleStreamEvent`. Refetch once so the snapshot
  // reconciles `run.status` (→ terminal) and the composer reflects the ended
  // run instead of lingering on `running`.
  useEffect(() => {
    if (follow && stream.status === 'done') void refetch();
  }, [follow, stream.status, refetch]);

  const handleCancelTurn = useRunCancellation({
    agentRunId,
    transport,
    phase,
    inputState: run?.input_state,
    refetch,
    refetchAndDetectDiscard,
    dispatchTurn,
    options,
  });

  useEffect(() => {
    if (!run) return;
    // Feed the snapshot into the machine. An active reading confirms a
    // submitted turn actually started (leaving the post-submit lag window), or
    // — once the snapshot's cursor has caught up past the boundary — lets a
    // genuinely new turn escape `ended` (a scheduler/other tab chaining the next
    // turn before we saw a settled snapshot). A settled reading reconciles
    // `ended`/`cancelling`/`running` back to idle; the `running` case is what
    // recovers from a boundary-less stream failure.
    //
    // `phase` is a dependency on purpose: entering `ended` doesn't change `run`
    // (and React Query's structural sharing can hand back the same object from
    // the boundary refetch), so without it an already-current new-turn snapshot
    // would never be re-fed and the escape would never run.
    if (isTerminalRunStatus(run.status)) {
      dispatchTurn({ type: 'RUN_ENDED' });
    } else if (
      run.input_state === 'turn_running' ||
      run.input_state === 'approval_required'
    ) {
      dispatchTurn({ type: 'TURN_CONFIRMED', streamCursor: run.stream_cursor ?? null });
    } else {
      dispatchTurn({ type: 'RECONCILED' });
    }
  }, [run, phase]);

  const handleLoaderPhrases = useCallback((turnId: string, phrases: string[]) => {
    if (!turnId || phrases.length === 0) return;
    setLoaderPhrasesByTurnId((current) => ({ ...current, [turnId]: phrases }));
  }, []);

  const loaderPhrases = useMemo(() => {
    const turnId = run?.latest_turn_id;
    if (!turnId) return undefined;
    return loaderPhrasesByTurnId[turnId];
  }, [loaderPhrasesByTurnId, run?.latest_turn_id]);

  // Bound the map to the current turn as the snapshot advances — only
  // `latest_turn_id` is ever read, so older entries are pure dead weight on a
  // long-lived run. A transient extra key can exist between a turn's SSE
  // loader-phrase event and the snapshot catching up to it as latest_turn_id.
  const latestTurnId = run?.latest_turn_id;
  useEffect(() => {
    if (!latestTurnId) return;
    setLoaderPhrasesByTurnId((current) => {
      const keys = Object.keys(current);
      if (keys.length === 0) return current;
      if (keys.length === 1 && keys[0] === latestTurnId) return current;
      const phrases = current[latestTurnId];
      return phrases ? { [latestTurnId]: phrases } : {};
    });
  }, [latestTurnId]);

  const items = useMemo(
    () => foldTranscript(store, { dropEmptyCancelledTurns }),
    [store, dropEmptyCancelledTurns]
  );

  return {
    run,
    isLoading,
    isError,
    error,
    items,
    turnInFlight: turnActive,
    cancelling: phase === 'cancelling',
    streamStatus: stream.status,
    streamDiagnostics: store.streamDiagnostics,
    handleTurnSubmitted,
    beginTurnOperation,
    handleCancelTurn,
    loaderPhrases,
    handleLoaderPhrases,
  };
};
